import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  Paper,
  CircularProgress,
  Chip,
  Fade,
  Grow,
  Zoom,
  Alert,
  AlertTitle,
  Divider,
  LinearProgress,
} from "@mui/material";
import {
  AccountBalanceWallet,
  Payment,
  QrCodeScanner,
  AccessTime,
  PhoneAndroid,
  CurrencyRupee,
  ArrowBack,
  Info,
  Warning,
} from "@mui/icons-material";
import { QRCodeCanvas } from "qrcode.react";
import AuthContext from "../store/AuthContext";
import { postJsonData } from "../network/ApiController";
import ApiEndpoints from "../network/ApiEndPoints";
import { apiErrorToast, okSuccessToast } from "../utils/ToastUtil";
import Loader from "../component/loading-screen/Loader";
import OutletRegistration from "../component/OutletRegistration";
import AEPS2View from "./aeps/AEPS2View";
import Aeps1OutletStatusIpay from "../component/Aeps1OutletStatusIpay";

const UpiAtmView = ({ resetView }) => {
  const { user, location } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState("");
  const [mobile, setMobile] = useState("");
  const [ipayId, setIpayId] = useState("");
  const [qrString, setQrString] = useState("");
  const [timer, setTimer] = useState(0);
  const [pollingTime, setPollingTime] = useState(0);
  const [qrExpired, setQrExpired] = useState(false);

  const handleSubmit = () => {
    if (!amount || !mobile) return apiErrorToast("All fields are required");

    if (mobile.length !== 10)
      return apiErrorToast("Enter valid 10 digit mobile number");

    const amt = Number(amount);

    if (!(amt >= 100 && amt <= 5000 && amt % 100 === 0)) {
      return apiErrorToast(
        "Enter amount between ₹100–₹5000 in multiples of ₹100"
      );
    }

    postJsonData(
      ApiEndpoints.UPI_ATM,
      {
        amount: amt,
        number: mobile,
        latitude: location?.lat,
        longitude: location?.long,
        pf: "web",
      },
      setLoading,
      (res) => {
        const response = res?.data?.data?.data;

        const qr = response?.qrString || response?.qr_string || response?.qr || "";
        const ipay_id = response?.ipay_id || response?.ipayId || "";

        if (!qr || !qr.startsWith("upi://")) {
          return apiErrorToast("Invalid QR");
        }

        setQrString(qr);
        setIpayId(ipay_id);
        setQrExpired(false);
        setTimer(Number(response?.displayExpirySec || 30));
        setPollingTime(300);
      }
    );
  };

  useEffect(() => {
    if (!qrString) return;

    if (timer === 0) {
      setQrString("");
      setQrExpired(true);
      return;
    }

    const t = setInterval(() => {
      setTimer((p) => p - 1);
    }, 1000);

    return () => clearInterval(t);
  }, [timer, qrString]);

  useEffect(() => {
    if (pollingTime <= 0) return;

    const t = setInterval(() => {
      setPollingTime((p) => p - 1);
    }, 1000);

    return () => clearInterval(t);
  }, [pollingTime]);

  useEffect(() => {
    if (!ipayId || pollingTime <= 0) return;

    const interval = setInterval(() => {
      postJsonData(
        ApiEndpoints.UPI_ATM_CHECK_STATUS,
        {
          number: mobile,
          amount: amount,
          id: user?.id,
          ipay_id: ipayId,
        },
        null,
        (res) => {
          const status = res?.data?.status;

          if (status === "SUCCESS" || status === "TXN_SUCCESS") {
            clearInterval(interval);
            setPollingTime(0);
            setQrString("");
            setQrExpired(false);
            setTimer(0);
            setAmount("");
            setMobile("");
            setIpayId("");
            okSuccessToast(`Transaction successful via UTR ${res?.data?.message || ""}`);
            return;
          }

          if (status === "FAILED" || status === "TXN_FAILED") {
            clearInterval(interval);
            setPollingTime(0);
            setQrString("");
            setQrExpired(false);
            apiErrorToast(res?.data?.message || "Transaction Failed, please try again");
            return;
          }
        }
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [ipayId]);

  const handleMobileChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 10) value = value.slice(0, 10);
    setMobile(value);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {!user?.instId ? (
        <Box textAlign="center" mt={10}>
          <Typography color="error">Outlet Not Registered</Typography>
          <OutletRegistration autoOpen />
        </Box>
      ) 
      // : user?.outlet_status === 0 ? (
      //   <Aeps1OutletStatusIpay open={true} />
      // ) 
      : (
        <Fade in timeout={500}>
          <Box
            sx={{
              minHeight: "100vh",
              // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              py: 4,
              px: 2,
            }}
          >
            <Grid container justifyContent="center" alignItems="center">
              <Grid item xs={12} sm={10} md={6} lg={5}>
                <Grow in timeout={800}>
                  <Paper
                    elevation={24}
                    sx={{
                      p: { xs: 3, sm: 5 },
                      borderRadius: "32px",
                      background: "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                      },
                    }}
                  >
                    {/* Header Section */}
                    <Box textAlign="center" mb={4}>
                      <Zoom in>
                        <Box
                          sx={{
                            width: 70,
                            height: 70,
                            margin: "0 auto",
                            borderRadius: "20px",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                          }}
                        >
                          <AccountBalanceWallet sx={{ fontSize: 40, color: "white" }} />
                        </Box>
                      </Zoom>
                      <Typography
                        variant="h4"
                        fontWeight="800"
                        sx={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        UPI ATM
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        Instant cash withdrawal via UPI QR
                      </Typography>
                    </Box>

                    <Loader loading={loading} />

                    {!qrString && !qrExpired ? (
                      <Fade in timeout={500}>
                        <Box>
                          <TextField
                            fullWidth
                            label="Mobile Number"
                            value={mobile}
                            onChange={handleMobileChange}
                            sx={{ mb: 3 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PhoneAndroid color="primary" />
                                  <Typography ml={0.5}>+91 |</Typography>
                                </InputAdornment>
                              ),
                            }}
                            placeholder="9876543210"
                          />

                          <TextField
                            fullWidth
                            label="Amount"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            sx={{ mb: 3 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CurrencyRupee color="primary" />
                                </InputAdornment>
                              ),
                            }}
                            placeholder="100, 200, 300... (Multiple of 100)"
                          />

                          <Button
                            fullWidth
                            size="large"
                            variant="contained"
                            onClick={handleSubmit}
                            sx={{
                              mb: 3,
                              py: 1.5,
                              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              "&:hover": {
                                background: "linear-gradient(135deg, #5a67d8 0%, #6b46a0 100%)",
                                transform: "translateY(-2px)",
                              },
                              transition: "all 0.3s ease",
                            }}
                            startIcon={<QrCodeScanner />}
                          >
                            Generate QR Code
                          </Button>

                          <Divider sx={{ my: 3 }}>
                            <Chip label="Important Information" icon={<Info />} />
                          </Divider>

                          <Alert
                            severity="warning"
                            icon={<Warning />}
                            sx={{
                              borderRadius: "16px",
                              "& .MuiAlert-message": { width: "100%" },
                            }}
                          >
                            <AlertTitle sx={{ fontWeight: "bold" }}>
                              Important Instructions
                            </AlertTitle>
                            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                              <li>
                                <strong style={{ color: "#d32f2f" }}>
                                  PhonePe & Google Pay payments are NOT accepted
                                </strong>
                              </li>
                              <li>Amount must be between ₹100 - ₹5000</li>
                              <li>Amount should be in multiples of ₹100</li>
                              <li>QR code expires in 30 seconds</li>
                            </Box>
                          </Alert>
                        </Box>
                      </Fade>
                    ) : qrString ? (
                      <Fade in timeout={500}>
                        <Box textAlign="center">
                          <Zoom in>
                            <Box
                              sx={{
                                p: 3,
                                background: "white",
                                borderRadius: "24px",
                                display: "inline-block",
                                boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                                mb: 3,
                              }}
                            >
                              <QRCodeCanvas value={qrString} size={220} />
                            </Box>
                          </Zoom>

                          <Box sx={{ mb: 3 }}>
                            <Chip
                              icon={<AccessTime />}
                              label={`QR expires in: ${timer}s`}
                              color={timer < 10 ? "error" : "warning"}
                              sx={{ fontSize: "16px", py: 2, px: 2, mb: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary" mt={1}>
                              Session Time Left: {formatTime(pollingTime)}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={(pollingTime / 300) * 100}
                              sx={{ mt: 1, height: 6, borderRadius: 3 }}
                            />
                          </Box>

                          <Alert severity="info" sx={{ borderRadius: "12px", mb: 2 }}>
                            Scan this QR code from any UPI app to complete payment
                          </Alert>
                        </Box>
                      </Fade>
                    ) : (
                      <Fade in timeout={500}>
                        <Box textAlign="center" py={4}>
                          <Box position="relative" display="inline-block">
                            <CircularProgress size={80} thickness={4} />
                            <Box
                              position="absolute"
                              top="50%"
                              left="50%"
                              sx={{ transform: "translate(-50%, -50%)" }}
                            >
                              <Payment color="primary" />
                            </Box>
                          </Box>

                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="primary"
                            mt={3}
                            gutterBottom
                          >
                            Processing Transaction...
                          </Typography>

                          <Typography variant="body2" color="text.secondary" mb={2}>
                            Please wait while we confirm your payment
                          </Typography>

                          <Alert severity="warning" sx={{ borderRadius: "12px", mb: 2 }}>
                            Do not close this screen or go back
                          </Alert>

                          <Box mt={3}>
                            <Chip
                              icon={<AccessTime />}
                              label={`Session Time Left: ${formatTime(pollingTime)}`}
                              variant="outlined"
                              color="primary"
                            />
                            <LinearProgress
                              variant="determinate"
                              value={(pollingTime / 300) * 100}
                              sx={{ mt: 2, height: 6, borderRadius: 3 }}
                            />
                          </Box>
                        </Box>
                      </Fade>
                    )}

                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => resetView(false)}
                      sx={{
                        mt: 3,
                        py: 1.2,
                        borderRadius: "12px",
                        borderColor: "#e0e0e0",
                        color: "#666",
                        "&:hover": {
                          borderColor: "#667eea",
                          backgroundColor: "rgba(102, 126, 234, 0.05)",
                        },
                      }}
                      startIcon={<ArrowBack />}
                    >
                      Back
                    </Button>
                  </Paper>
                </Grow>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      )}
    </>
  );
};

export default UpiAtmView;